import PropTypes from 'prop-types';
import { cloneElement, isValidElement, useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import menu2Fill from '@iconify/icons-eva/menu-2-fill';
import { alpha, styled } from '@mui/material/styles';
import { Badge, Box, Stack, AppBar, Toolbar, IconButton, Tooltip, Typography } from '@mui/material';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import useCollapseDrawer from 'hooks/useCollapseDrawer';
import { MHidden } from 'components/@material-extend';
import AccountPopover from './AccountPopover';
import useAuth from 'hooks/useAuth';
import { sanitizeShortcuts, buildShortcutKey, flattenSidebarConfig } from 'utils/sidebarShortcuts';
import { useNavigate } from 'react-router-dom';
import closeFill from '@iconify/icons-eva/close-fill';
import gridFill from '@iconify/icons-eva/grid-fill';
import ApiServiceFecth from 'service/ApiServiceFecth';
import endPoints from 'endPoints/endPoints';

const DRAWER_WIDTH = 220;
const COLLAPSE_WIDTH = 90;

const APPBAR_MOBILE = 34;
const APPBAR_DESKTOP = 62;

const RootStyle = styled(AppBar)(({ theme }) => ({
  boxShadow: 'none',
  backdropFilter: 'blur(6px)',
  WebkitBackdropFilter: 'blur(6px)',
  backgroundColor: alpha(theme.palette.background.default, 0.72),
  [theme.breakpoints.up('lg')]: {
    width: `calc(100% - ${DRAWER_WIDTH + 1}px)`
  }
}));

const ToolbarStyle = styled(Toolbar)(({ theme }) => ({
  minHeight: APPBAR_MOBILE,
  [theme.breakpoints.up('lg')]: {
    minHeight: APPBAR_DESKTOP,
    padding: theme.spacing(0, 2)
  }
}));

DashboardNavbar.propTypes = {
  onOpenSidebar: PropTypes.func,
  sidebarConfig: PropTypes.array,
};

const DND_TYPE = 'application/enpiso-shortcut';
const DND_REORDER_TYPE = 'application/enpiso-shortcut-reorder';
const MAX_SHORTCUTS = 12;

export default function DashboardNavbar({ onOpenSidebar, handlePermitSound, sidebarConfig = [] }) {
  const { isCollapse } = useCollapseDrawer();
  const { menuShortcuts = [], saveMenuShortcuts, checkAccess } = useAuth();
  const [localShortcuts, setLocalShortcuts] = useState([]);
  const [openAlertsCount, setOpenAlertsCount] = useState(0);
  const navigate = useNavigate();
  const persistTimeoutRef = useRef(null);
  const sidebarShortcutMap = useMemo(() => {
    const map = {};
    flattenSidebarConfig(sidebarConfig).forEach((item) => {
      map[item.shortcut_key] = item;
    });
    return map;
  }, [sidebarConfig]);

  useEffect(() => {
    setLocalShortcuts(sanitizeShortcuts(menuShortcuts));
  }, [menuShortcuts]);

  const visibleShortcuts = useMemo(
    () => sanitizeShortcuts(localShortcuts).filter((shortcut) => checkAccess(shortcut.permission_key || '')),
    [localShortcuts, checkAccess]
  );

  useEffect(() => {
    const normalizedLocal = sanitizeShortcuts(localShortcuts);
    const normalizedRemote = sanitizeShortcuts(menuShortcuts);

    if (JSON.stringify(normalizedLocal) === JSON.stringify(normalizedRemote)) {
      return () => {};
    }

    clearTimeout(persistTimeoutRef.current);
    persistTimeoutRef.current = setTimeout(() => {
      saveMenuShortcuts(normalizedLocal).catch(() => {});
    }, 500);

    return () => clearTimeout(persistTimeoutRef.current);
  }, [localShortcuts, menuShortcuts, saveMenuShortcuts]);

  useEffect(() => {
    let isMounted = true;

    const loadAlertSummary = async () => {
      const response = await ApiServiceFecth.get({ url: endPoints.admin.infra.alerts.summary });
      if (!response?.ok || !isMounted) return;
      const json = await response.json();
      const openCount = Number(json?.data?.open_count || 0);
      setOpenAlertsCount(openCount);
    };

    loadAlertSummary();
    const timer = setInterval(loadAlertSummary, 60000);
    return () => {
      isMounted = false;
      clearInterval(timer);
    };
  }, []);

  const upsertShortcut = (shortcut) => {
    setLocalShortcuts((prev) => {
      const normalized = sanitizeShortcuts(prev);
      const exists = normalized.find((item) => item.shortcut_key === shortcut.shortcut_key);
      if (exists) {
        return normalized;
      }
      if (normalized.length >= MAX_SHORTCUTS) {
        return normalized;
      }
      return [...normalized, shortcut];
    });
  };

  const onDropZoneDrop = (event) => {
    event.preventDefault();

    const raw = event.dataTransfer.getData(DND_TYPE);
    if (!raw) {
      return;
    }

    try {
      const payload = JSON.parse(raw);
      const shortcut = {
        shortcut_key: payload.shortcut_key || buildShortcutKey(payload),
        title: payload.title,
        path: payload.path,
        permission_key: payload.permission_key || null,
        section: payload.section || null,
      };
      upsertShortcut(shortcut);
    } catch (error) {
      // no-op
    }
  };

  const onShortcutDragStart = (event, shortcutKey) => {
    event.dataTransfer.setData(DND_REORDER_TYPE, shortcutKey);
    event.dataTransfer.effectAllowed = 'move';
  };

  const onShortcutDrop = (event, targetKey) => {
    event.preventDefault();
    const draggingKey = event.dataTransfer.getData(DND_REORDER_TYPE);
    if (!draggingKey || draggingKey === targetKey) {
      return;
    }

    setLocalShortcuts((prev) => {
      const list = [...sanitizeShortcuts(prev)];
      const fromIndex = list.findIndex((item) => item.shortcut_key === draggingKey);
      const toIndex = list.findIndex((item) => item.shortcut_key === targetKey);
      if (fromIndex < 0 || toIndex < 0) {
        return prev;
      }

      const [dragged] = list.splice(fromIndex, 1);
      list.splice(toIndex, 0, dragged);
      return list;
    });
  };

  const resolveShortcutIcon = (shortcut) => {
    const fromSidebar = sidebarShortcutMap[shortcut.shortcut_key]?.icon;
    if (isValidElement(fromSidebar)) {
      return cloneElement(fromSidebar, {
        fontSize: 'small',
        sx: { ...(fromSidebar.props?.sx || {}), fontSize: 18, color: 'primary.main' },
      });
    }
    return <Icon icon={gridFill} width={18} height={18} style={{ color: 'var(--mui-palette-primary-main)' }} />;
  };

  return (
    <RootStyle
      sx={{
        ...(isCollapse && {
          width: { lg: `calc(100% - ${COLLAPSE_WIDTH}px)` }
        })
      }}
    >
      <ToolbarStyle onClick={handlePermitSound}>
        <MHidden width="lgUp">
          <IconButton onClick={onOpenSidebar} sx={{ mr: 1, color: 'text.primary' }}>
            <Icon icon={menu2Fill} />
          </IconButton>
        </MHidden>

        <Box
          onDragOver={(event) => event.preventDefault()}
          onDrop={onDropZoneDrop}
          sx={{
            flexGrow: 1,
            minHeight: 72,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1,
            border: (theme) => `1px dashed ${alpha(theme.palette.primary.main, 0.35)}`,
            borderRadius: 1,
            overflowX: 'auto',
            py: 0.75,
            '&::-webkit-scrollbar': { height: 4 },
          }}
        >
          {visibleShortcuts.length === 0 ? (
            <Typography variant="caption" color="text.secondary">
              Arrastra aquí opciones del menú para crear accesos directos
            </Typography>
          ) : (
            visibleShortcuts.map((shortcut) => (
              <Tooltip key={shortcut.shortcut_key} title={shortcut.path}>
                <Box
                  draggable
                  onDragStart={(event) => onShortcutDragStart(event, shortcut.shortcut_key)}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={(event) => onShortcutDrop(event, shortcut.shortcut_key)}
                  onClick={() => navigate(shortcut.path)}
                  sx={{
                    position: 'relative',
                    width: 86,
                    minWidth: 86,
                    height: 56,
                    borderRadius: 1.5,
                    border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.28)}`,
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    pt: 0.4,
                    px: 0.5,
                    '&:hover': {
                      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.16),
                    },
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={(event) => {
                      event.stopPropagation();
                      setLocalShortcuts((prev) => prev.filter((item) => item.shortcut_key !== shortcut.shortcut_key));
                    }}
                    sx={{
                      position: 'absolute',
                      top: -6,
                      right: -6,
                      width: 16,
                      height: 16,
                      bgcolor: 'background.paper',
                      border: (theme) => `1px solid ${theme.palette.divider}`,
                      '&:hover': { bgcolor: 'error.lighter', color: 'error.main' },
                    }}
                  >
                    <Icon icon={closeFill} width={10} height={10} />
                  </IconButton>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.25 }}>
                    {resolveShortcutIcon(shortcut)}
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      lineHeight: 1.1,
                      textAlign: 'center',
                      color: 'primary.main',
                      fontWeight: 600,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      maxWidth: '100%',
                    }}
                  >
                    {shortcut.title}
                  </Typography>
                </Box>
              </Tooltip>
            ))
          )}
        </Box>

        <Stack direction="row" alignItems="center" spacing={{ xs: 0.25, sm: 0.5 }} sx={{ ml: 1 }}>
          <Tooltip title="Alertas de infraestructura">
            <IconButton onClick={() => navigate('/app/developer/infra/alerts')}>
              <Badge
                color="error"
                badgeContent={openAlertsCount > 99 ? '99+' : openAlertsCount}
                invisible={openAlertsCount <= 0}
              >
                <NotificationsNoneOutlinedIcon />
              </Badge>
            </IconButton>
          </Tooltip>
          <AccountPopover />
        </Stack>
      </ToolbarStyle>
    </RootStyle>
  );
}
